import { ChangeDetectionStrategy, Component, computed, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { delay, from, map, Observable, switchMap, take } from 'rxjs';
import { IonicModule } from '@ionic/angular';
import { Router } from '@angular/router';
import {
  ActionModalComponent,
  AnalyticsService,
  AppRoutes,
  ENV_CONFIG,
  EnvConfig,
  MODAL_TOKEN,
  ModalProvider,
  NotificationsRoutes,
  PLATFORM_TOKEN,
  PlatformProvider,
  PresentModalComponent,
  ProfileAnalyticsEvent,
  ProfileRoutes,
  USER_DATA_TOKEN,
  UserDataProvider,
  UserModel
} from '@core';
import {
  GetAlertsSettingsQueryHandler,
  GetAlertsSettingsQueryResult,
  UnregisterTokenCommandHandler
} from '@notifications';
import { NotificationsAlertsService } from '@notifications/infra';
import { getNewVersionNative, VersionConfigSyncService } from '@update';
import { IsPasswordAccountQueryHandler, LogoutCommandHandler } from '../../../../../auth/src/lib/application/handlers';
import { IsNewestVersionQueryHandler } from '../../application/handlers';
import { UserRoleEnum } from '@core-http';
import { OrIconComponent } from '@ui-components';

@Component({
  selector: 'lib-profile-settings',
  templateUrl: './profile-settings.component.html',
  styleUrls: ['./profile-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [IonicModule, OrIconComponent],
  providers: [
    UnregisterTokenCommandHandler,
    IsNewestVersionQueryHandler,
    GetAlertsSettingsQueryHandler,
    NotificationsAlertsService
  ]
})
export class ProfileSettingsComponent {
  private readonly env: EnvConfig = inject(ENV_CONFIG);
  private readonly logoutCommandHandler: LogoutCommandHandler = inject(LogoutCommandHandler);
  private readonly router: Router = inject(Router);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly userDataProvider: UserDataProvider = inject(USER_DATA_TOKEN);
  private readonly unregisterTokenCommandHandler: UnregisterTokenCommandHandler = inject(UnregisterTokenCommandHandler);
  private readonly isNewestVersionQueryHandler: IsNewestVersionQueryHandler = inject(IsNewestVersionQueryHandler);
  private readonly platformProvider: PlatformProvider = inject(PLATFORM_TOKEN);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly versionConfigSyncService: VersionConfigSyncService = inject(VersionConfigSyncService);
  private readonly getAlertsSettingsQueryHandler: GetAlertsSettingsQueryHandler = inject(GetAlertsSettingsQueryHandler);
  private readonly isPasswordAccountQueryHandler: IsPasswordAccountQueryHandler = inject(IsPasswordAccountQueryHandler);

  private readonly userData$: Observable<UserModel> = this.userDataProvider.userData$;
  private readonly isNewestVersion$: Observable<boolean> = this.isNewestVersionQueryHandler.isNewestVersion();
  private readonly isAlertsEnabled$: Observable<boolean> = this.getAlertsSettingsQueryHandler
    .getAlertsSettings()
    .pipe(map((settings: GetAlertsSettingsQueryResult) => !!settings?.isEnabled));

  readonly userData: Signal<UserModel | undefined> = toSignal(this.userData$);
  readonly isNewestVersion: Signal<boolean | undefined> = toSignal(this.isNewestVersion$);
  readonly isAlertsEnabled: Signal<boolean> = toSignal(this.isAlertsEnabled$, { initialValue: false });
  readonly canChangePassword: Signal<boolean> = toSignal(this.isPasswordAccountQueryHandler.isPasswordAccount(), {
    initialValue: false
  });
  readonly shouldShowAdminPanel: Signal<boolean> = computed(() => this.userData()?.role === UserRoleEnum.ADMIN);
  readonly isNativeRun: boolean = this.platformProvider.isNativeRun;

  logout(): void {
    this.analyticsService.buttonClick(ProfileAnalyticsEvent.Logout);

    this.modalProvider
      .showModal$({
        component: ActionModalComponent,
        componentProps: {
          header: 'Wylogowanie',
          message: 'Czy na pewno chcesz się wylogować?',
          btnOk: 'Wyloguj się',
          btnCancel: 'Anuluj',
          action: () => this.performLogout()
        },
        cssClass: 'present-modal'
      })
      .pipe(take(1))
      .subscribe();
  }

  editProfile(): void {
    this.analyticsService.buttonClick(ProfileAnalyticsEvent.EditProfile);
    this.router.navigate([`${AppRoutes.PROFILE}/${ProfileRoutes.EDIT}`]);
  }

  goToChangePassword(): void {
    this.analyticsService.buttonClick(ProfileAnalyticsEvent.ChangePassword);
    this.router.navigate([`${AppRoutes.PROFILE}/${ProfileRoutes.CHANGE_PASSWORD}`]);
  }

  goToMyGames(): void {
    this.analyticsService.buttonClick(ProfileAnalyticsEvent.MyGames);
    this.router.navigate([AppRoutes.MY_GAMES]);
  }

  goToBlockedUsers(): void {}

  goToTrophies(): void {
    this.analyticsService.buttonClick(ProfileAnalyticsEvent.Trophies);
    this.router.navigate([`${AppRoutes.PROFILE}/${ProfileRoutes.ALL_TROPHIES}`]);
  }

  openContact(): void {
    this.analyticsService.buttonClick(ProfileAnalyticsEvent.Contact);
    this.router.navigate([`${AppRoutes.PROFILE}/${ProfileRoutes.CONTACT}`]);
  }

  goToAlertsSettings(): void {
    this.analyticsService.buttonClick(ProfileAnalyticsEvent.Alerts, { isEnabled: this.isAlertsEnabled() });
    this.router.navigate([`${AppRoutes.NOTIFICATIONS}/${NotificationsRoutes.ALERTS}`]);
  }

  openInstagram(): void {
    window.open('https://www.instagram.com/orlikfy');
  }

  openFacebook(): void {
    window.open('https://www.facebook.com/orlikfy');
  }

  goToAdminPanel(): void {
    this.router.navigate([AppRoutes.ADMIN_PANEL]);
  }

  updateApp(): void {
    if (!this.platformProvider.isNativeRun) return;

    this.analyticsService.buttonClick(ProfileAnalyticsEvent.CheckUpdate, {
      isNewestVersion: !!this.isNewestVersion()
    });

    from(this.versionConfigSyncService.sync())
      .pipe(
        delay(500),
        switchMap(() => this.isNewestVersion$),
        take(1),
        switchMap((version: boolean) => (version ? this.hasNewestVersion() : this.showUpdateModal()))
      )
      .subscribe();
  }

  showUpdateModal(): Observable<void> {
    return this.modalProvider.showModal$({
      component: ActionModalComponent,
      componentProps: {
        header: 'Dostępna aktualizacja!',
        message: 'Nowa wersja aplikacji jest dostępna. Pobierz ją, aby korzystać z nowych funkcji!',
        btnOk: 'Pobierz aktualizację',
        btnCancel: 'Anuluj',
        action: () => getNewVersionNative(this.platformProvider, this.env)
      },
      cssClass: 'present-modal'
    });
  }

  hasNewestVersion(): Observable<void> {
    return this.modalProvider.showModal$({
      component: PresentModalComponent,
      componentProps: {
        header: 'Brak aktualizacji',
        message: 'Masz najnowszą wersję!',
        btnTxt: 'Zamknij'
      },
      cssClass: 'present-modal'
    });
  }

  getTermsOfService(): void {
    window.open(
      'https://firebasestorage.googleapis.com/v0/b/orlikfy-infra-prod.appspot.com/o/docs%2FOrlikfy%20Regulamin.pdf?alt=media&token=337ce6fa-1b40-4ed9-a89d-4a917d332e91'
    );
  }

  getPolicyPrivacy(): void {
    window.open(
      'https://firebasestorage.googleapis.com/v0/b/orlikfy-infra-prod.appspot.com/o/docs%2FOrlikfy%20Polityka%20Prywatnosci.pdf?alt=media&token=ad93ba59-a2ef-445e-b4d2-5d75da613b9f'
    );
  }

  private performLogout(): void {
    this.unregisterTokenCommandHandler
      .unregister()
      .pipe(
        take(1),
        switchMap(() => this.logoutCommandHandler.logout()),
        switchMap(() => this.router.navigate([`/${AppRoutes.AUTH}`]))
      )
      .subscribe();
  }
}
